import { type FC, useCallback, useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import {
  Button,
  ButtonVariant,
  ModalHeader,
  Status,
  SwitchButton,
  TooltipText,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  CompanySubscriptionDetails,
  CompanySubscriptionDetailsEntry,
  FullCompanyApiType,
  PaymentProviderCustomerDetails,
  SubscriptionInvoice,
} from "../../../../util/types";
import { SubscriptionOptionEnum } from "@shared_packages/toolkit/types";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessage } from "../../../../util/apiErrorMessage";
import { useAuthUserContext } from "../../../../context/AdministratorContextProvider";
import { useModalManager } from "../../../../context/ModalManager";
import SubscriptionWizardModal, {
  getDefaultTrialDays,
} from "./SubscriptionWizardModal";
import { SubscriptionPricingBreakdown } from "./TransactionFeesScheduleViewer";
import { useInvoicePdfDownload } from "../../billing/hooks/useInvoicePdfDownload";
import {
  SUBSCRIPTION_BILLING_TABS,
  SubscriptionBillingTabOption,
  PILL_STATUS_LABEL,
  resolveSubscriptionPillStatus,
  isDeletedSubscriptionStatus,
} from "./subscriptionBillingTabs.config";
import { SubscriptionCollectionsCases } from "./SubscriptionCollectionsCases";
import { SubscriptionHistory } from "./SubscriptionHistory";

// ─────────────────────────────────────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────────────────────────────────────

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

// pulseGlow removed — unused

const animateIn = css`
  animation: ${fadeSlideIn} 0.35s ease-out both;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Styled components
// ─────────────────────────────────────────────────────────────────────────────

const Wrap = styled.div`
  margin-top: 20px;
`;

const PillBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
`;

const PillTab = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid ${(p) => (p.$active ? "#027aff" : "#d8d8d8")};
  background: ${(p) => (p.$active ? "#eef6ff" : "#fff")};
  color: ${(p) => (p.$active ? "#027aff" : "#444")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const PillBadge = styled.span<{ $tone?: string }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 999px;
  background: ${(p) => p.$tone ?? "#eee"};
  color: #333;
`;

const EmptyTabState = styled.div`
  border: 1px dashed #d8d8d8;
  border-radius: 12px;
  padding: 28px 20px;
  text-align: center;
  color: #888;
  font-size: 14px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PRECONFIGURATION_UNAVAILABLE_MESSAGE =
  "Preconfiguration is currently unavailable. The user must purchase a subscription before it can be configured.";
const PRECONFIGURATION_AVAILABLE_MESSAGE =
  "Use preconfiguration to prepare this subscription before the customer activates it.";

const TabHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
`;

const SectionHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  @media (max-width: 767px) {
    flex-wrap: wrap;
    gap: 10px;
    > button {
      flex: 1 1 100%;
    }
  }
`;

const SubscriptionConfirmedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  @media (max-width: 767px) {
    flex: 1 1 100%;
    > :first-child {
      flex: 1 1 auto;
    }
  }
`;

const Modal = styled.div`
  max-height: 90vh;
  overflow: auto;
`;

const ModalContent = styled.div`
  padding: 30px;
`;

const ModalButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

// ── Cockpit card ──────────────────────────────────────────────────────────────

const Cockpit = styled.div`
  border: 1px solid #d8d8d8;
  border-radius: 14px;
  background: #fff;
  overflow: hidden;
  ${animateIn}
`;

const CockpitHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #f7f8fa;
  border-bottom: 1px solid #e8e8e8;
  gap: 12px;
  flex-wrap: wrap;
`;

const PlanTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111;
  letter-spacing: -0.01em;
`;

const PlanMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PlanTag = styled.span<{
  $variant?: "purple" | "blue" | "green" | "orange" | "red" | "gray";
}>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $variant }) =>
    $variant === "purple"
      ? "#f3f0ff"
      : $variant === "blue"
      ? "#eef6ff"
      : $variant === "green"
      ? "#f0fff4"
      : $variant === "orange"
      ? "#fff8ef"
      : $variant === "red"
      ? "#fff0f0"
      : "#f4f4f4"};
  color: ${({ $variant }) =>
    $variant === "purple"
      ? "#7c3aed"
      : $variant === "blue"
      ? "#027aff"
      : $variant === "green"
      ? "#2a7"
      : $variant === "orange"
      ? "#c47a00"
      : $variant === "red"
      ? "#c00"
      : "#555"};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "purple"
        ? "#c4b5fd"
        : $variant === "blue"
        ? "#9bc8ff"
        : $variant === "green"
        ? "#86efac"
        : $variant === "orange"
        ? "#fcd34d"
        : $variant === "red"
        ? "#fca5a5"
        : "#e0e0e0"};
`;

// ── Cancellation banner ───────────────────────────────────────────────────────

const CancellationBanner = styled.div<{ $variant: "scheduled" | "cancelled" }>`
  margin: 0;
  padding: 12px 20px;
  border-bottom: 1px solid
    ${(p) => (p.$variant === "cancelled" ? "#fca5a5" : "#fcd34d")};
  background: ${(p) => (p.$variant === "cancelled" ? "#fff5f5" : "#fff8ec")};
  color: ${(p) => (p.$variant === "cancelled" ? "#7f1d1d" : "#7c4a00")};
  font-size: 13px;
  line-height: 1.5;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  ${animateIn}
`;

const CancellationBannerIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  flex-shrink: 0;
`;

const CancellationBannerBody = styled.div`
  flex: 1;
`;

const CancellationBannerReason = styled.div`
  margin-top: 4px;
  font-size: 12px;
  opacity: 0.85;
`;

// ── Sections ──────────────────────────────────────────────────────────────────

const CSection = styled.div<{ $delay?: number }>`
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  ${animateIn}
  animation-delay: ${(p) => (p.$delay ?? 0) * 0.08}s;
  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const SLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

// ── Metrics row ───────────────────────────────────────────────────────────────

const MetricRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 14px;
`;

const MetricBox = styled.div<{ $accent: string }>`
  background: #fafafa;
  border: 1px solid #eee;
  border-top: 3px solid ${(p) => p.$accent};
  border-radius: 8px;
  padding: 10px 12px;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const MLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 5px;
`;

const MValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: #111;
`;

const MSub = styled.div`
  font-size: 10px;
  color: #bbb;
  margin-top: 3px;
`;

// ── Timeline ──────────────────────────────────────────────────────────────────

const TimelineWrap = styled.div`
  position: relative;
  margin: 22px 0 20px;
`;

const TLTrack = styled.div`
  position: relative;
  height: 10px;
  border-radius: 5px;
  background: #eeeeee;
`;

const TLFill = styled.div<{ $pct: number; $color: string }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${(p) => Math.min(p.$pct, 100)}%;
  background: ${(p) => p.$color};
  border-radius: 5px;
  transition: width 0.3s ease;
`;

const TLDot = styled.div<{ $left: number; $color: string; $size?: number }>`
  position: absolute;
  top: 50%;
  left: ${(p) => p.$left}%;
  transform: translate(-50%, -50%);
  width: ${(p) => p.$size ?? 14}px;
  height: ${(p) => p.$size ?? 14}px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  border: 2px solid #fff;
  box-shadow: 0 0 0 1.5px ${(p) => p.$color};
  z-index: 2;
`;

const TodayPin = styled.div<{ $left: number }>`
  position: absolute;
  top: -6px;
  left: ${(p) => p.$left}%;
  transform: translateX(-50%);
  width: 2px;
  height: 22px;
  background: #333;
  border-radius: 1px;
  z-index: 3;
  &::after {
    content: "today";
    position: absolute;
    top: -14px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 9px;
    color: #555;
    font-weight: 600;
    white-space: nowrap;
  }
`;

const TLLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: #bbb;
  margin-top: 6px;
`;

const TLLegend = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 5px;
  flex-wrap: wrap;
`;

const TLLegendDot = styled.span<{ $color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => p.$color};
  margin-right: 3px;
  vertical-align: middle;
`;

const TLLegendItem = styled.span`
  font-size: 10px;
  color: #999;
`;

// ── Next phase preview ────────────────────────────────────────────────────────

const NextPhase = styled.div`
  margin-top: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #f5f3ff;
  border: 1px dashed #c4b5fd;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const NextPhaseContent = styled.div``;

const NextPhasePrimary = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #7c3aed;
`;

const NextPhaseSub = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 2px;
`;

// ── Countdown chip ────────────────────────────────────────────────────────────

const Chip = styled.div<{ $variant?: "warn" | "ok" | "danger" }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $variant }) =>
    $variant === "danger"
      ? "#fff0f0"
      : $variant === "warn"
      ? "#fff8ef"
      : "#f0f7ff"};
  border: 1px solid
    ${({ $variant }) =>
      $variant === "danger"
        ? "#fca5a5"
        : $variant === "warn"
        ? "#fcd34d"
        : "#9bc8ff"};
  color: ${({ $variant }) =>
    $variant === "danger"
      ? "#c00"
      : $variant === "warn"
      ? "#c47a00"
      : "#027aff"};
`;

// ── KV rows (general) ─────────────────────────────────────────────────────────

const KV = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
  gap: 16px;
  &:last-child {
    border-bottom: none;
  }
`;

const KKey = styled.span`
  font-size: 12px;
  color: #888;
  flex-shrink: 0;
`;

const KVal = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #222;
  text-align: right;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// ── Inline mini button ────────────────────────────────────────────────────────

const MiniBtn = styled.button`
  font-size: 11px;
  font-weight: 600;
  color: #888;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    border-color: #c00;
    color: #c00;
    background: #fff5f5;
  }
  &:disabled {
    opacity: 0.4;
    cursor: default;
    pointer-events: none;
  }
`;

// ── Stripe payments card ──────────────────────────────────────────────────────

const StripeCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 10px;
  background: #fafafa;
`;

const StripeCardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const StripeCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ── Invoice table ─────────────────────────────────────────────────────────────

const InvoiceWrap = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  background: #fff;
  padding: 14px 18px;
  margin-top: 12px;
  ${animateIn}
  animation-delay: 0.4s;
`;

const InvoiceTableScroll = styled.div`
  overflow-x: auto;
`;

const InvoiceTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  margin-top: 8px;
`;

const Th = styled.th`
  text-align: left;
  font-weight: 600;
  color: #aaa;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 8px;
  border-bottom: 1px solid #eee;
`;

const Td = styled.td`
  padding: 7px 8px;
  color: #333;
  border-bottom: 1px solid #f8f8f8;
  &:last-child {
    text-align: right;
  }
`;

const StatusBadge = styled.span<{ $paid?: boolean }>`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$paid ? "#f0fff4" : "#fff0f0")};
  color: ${(p) => (p.$paid ? "#2a7" : "#c55")};
`;

const RowActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const ViewButton = styled.button`
  padding: 3px 8px;
  background: #f3f4f6;
  color: #374151;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
  &:hover {
    background: #e5e7eb;
  }
`;

const DownloadButton = styled.button`
  padding: 3px 8px;
  background: #eef2ff;
  color: #3730a3;
  border: none;
  border-radius: 6px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 500;
  white-space: nowrap;
  &:hover:not(:disabled) {
    background: #e0e7ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BillingProviderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background: #fafafa;
  @media (max-width: 767px) {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const BillingProviderLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
`;

const BillingProviderCaption = styled.div`
  font-size: 12px;
  color: #8a8a8a;
  margin-top: 2px;
`;

const BillingProviderSelect = styled.select`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  cursor: pointer;
  min-width: 0;
`;

const BillingProviderConsent = styled.div`
  font-size: 12px;
  color: #6b7280;
`;

const ProviderTabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
`;

const ProviderTab = styled.button<{ $active?: boolean }>`
  font-size: 12px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 20px;
  border: 1px solid ${(p) => (p.$active ? "#027AFF" : "#dbe3eb")};
  background: ${(p) => (p.$active ? "#027AFF14" : "#fff")};
  color: ${(p) => (p.$active ? "#027AFF" : "#495b6c")};
  cursor: pointer;
  &:hover {
    border-color: #027aff;
  }
`;

const ProviderCard = styled.div<{ $softDeleted?: boolean }>`
  border: 1px solid ${(p) => (p.$softDeleted ? "#fca5a5" : "#e5e7eb")};
  border-radius: 10px;
  padding: 12px 14px;
  background: ${(p) => (p.$softDeleted ? "#fff7f7" : "#fff")};
  margin-bottom: 10px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const ProviderCardHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  margin-bottom: 10px;
`;

const ProviderBadge = styled.span<{
  $variant?: "default" | "success" | "warning" | "danger" | "muted";
}>`
  display: inline-block;
  padding: 2px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  background: ${(p) =>
    p.$variant === "danger"
      ? "#fee2e2"
      : p.$variant === "warning"
      ? "#fff7ed"
      : p.$variant === "muted"
      ? "#f3f4f6"
      : "#ecfdf5"};
  color: ${(p) =>
    p.$variant === "danger"
      ? "#991b1b"
      : p.$variant === "warning"
      ? "#c2410c"
      : p.$variant === "muted"
      ? "#6b7280"
      : "#047857"};
`;

const getProviderStatusBadge = (
  status: string | null | undefined
): {
  variant: "success" | "warning" | "danger" | "muted";
  label: string;
} => {
  switch ((status ?? "").toLowerCase()) {
    case "active":
      return { variant: "success", label: "Active" };
    case "requires_update":
      return { variant: "warning", label: "Requires update" };
    case "expired":
      return { variant: "danger", label: "Expired" };
    default:
      return { variant: "muted", label: status || "—" };
  }
};

const ProviderKV = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  gap: 4px 12px;
  font-size: 12px;
  & + & {
    margin-top: 4px;
  }
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
    gap: 2px;
    & + & {
      margin-top: 8px;
    }
  }
`;

const ProviderK = styled.div`
  color: #9ca3af;
  font-weight: 600;
`;

const ProviderV = styled.div`
  color: #374151;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const ProviderLink = styled.a`
  color: #027aff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const NoProviderData = styled.div`
  font-size: 13px;
  color: #9ca3af;
  padding: 16px 4px;
`;

// LoadingText removed — replaced by skeleton UI

const SkeletonBar = styled.div<{ $w?: string; $h?: string }>`
  width: ${(p) => p.$w ?? "100%"};
  height: ${(p) => p.$h ?? "12px"};
  border-radius: 6px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e4e4e4 37%, #f0f0f0 63%);
  background-size: 400px 100%;
  animation: ${shimmer} 1.4s ease infinite;
`;

const SkeletonRow = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
`;

const SkeletonSection = styled.div`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const EmptyCard = styled.div`
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 20px;
  color: #aaa;
  font-size: 13px;
  background: #fafafa;
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PreviewCard = styled.div`
  border: 1px dashed #c0c8d0;
  border-radius: 12px;
  background: #f8f9fb;
  padding: 20px 24px;
  position: relative;
  overflow: hidden;
  animation: ${fadeSlideIn} 0.3s ease;
`;

const PreviewOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(248, 249, 251, 0) 0%,
    rgba(248, 249, 251, 0.6) 100%
  );
  pointer-events: none;
  z-index: 1;
`;

const PreviewBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #f5a623;
  background: #fff8ec;
  border: 1px solid #f5c878;
  border-radius: 20px;
  padding: 3px 10px;
  z-index: 2;
`;

const PreviewTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #495b6c;
  margin-bottom: 14px;
`;

const PreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 10px;
  margin-bottom: 10px;
`;

const PreviewItem = styled.div`
  background: #fff;
  border: 1px solid #e8eaed;
  border-radius: 8px;
  padding: 10px 12px;
`;

const PreviewItemLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 3px;
`;

const PreviewItemValue = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #495b6c;
`;

const PreviewHint = styled.div`
  font-size: 12px;
  color: #aaa;
  text-align: center;
  margin-top: 12px;
`;

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const fmt = (pence: number | null | undefined) =>
  pence == null ? "—" : `£${(pence / 100).toFixed(2)}`;

/** Format a PENCE value (levelUsed, deltaVol — Fena billing convention).
 *  Divides by 100 and adds the £ prefix. Matches `fmt` above and
 *  `formatPence` in sharedTableStyles. */
const fmtVol = (pence: number | null | undefined) =>
  pence == null ? "—" : `£${(Number(pence) / 100).toFixed(2)}`;

const fmtDate = (iso?: string | null) =>
  !iso
    ? "—"
    : new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

const fmtUnix = (ts?: number) =>
  !ts
    ? "—"
    : new Date(ts * 1000).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });

function elapsedPct(start?: string | null, end?: string | null): number {
  if (!start || !end) return 0;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  const now = Date.now();
  if (e <= s) return 0;
  return Math.min(100, Math.max(0, ((now - s) / (e - s)) * 100));
}

function daysLeft(iso?: string | null): number | null {
  if (!iso) return null;
  return Math.max(
    0,
    Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000)
  );
}

function pctOnTrack(date: Date, start: string, end: string): number {
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (e <= s) return 0;
  return Math.min(98, Math.max(2, ((date.getTime() - s) / (e - s)) * 100));
}

const INTERVAL_MS: Record<string, number> = {
  daily: 23 * 3600_000,
  weekly: 6 * 24 * 3600_000,
  biweekly: 13 * 24 * 3600_000,
};

function interimDatesInPeriod(
  periodStart: string,
  periodEnd: string,
  interval: string
): Date[] {
  const ms = INTERVAL_MS[interval.toLowerCase()];
  if (!ms) return [];
  const end = new Date(periodEnd).getTime();
  const dates: Date[] = [];
  let t = new Date(periodStart).getTime() + ms;
  while (t < end) {
    dates.push(new Date(t));
    t += ms;
  }
  return dates;
}

function nextInterimCharge(
  lastCharge?: string | null,
  interval?: string | null
): Date | null {
  if (!lastCharge || !interval) return null;
  const ms = INTERVAL_MS[interval.toLowerCase()];
  if (!ms) return null;
  return new Date(new Date(lastCharge).getTime() + ms);
}

function fmtHours(hours: number): string {
  if (hours < 1) return "< 1h";
  if (hours < 24) return `${Math.floor(hours)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.floor(hours % 24);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function hoursUntil(d: Date): number {
  return Math.max(0, (d.getTime() - Date.now()) / 3600_000);
}

function currSym(currency?: string): string {
  switch ((currency ?? "gbp").toLowerCase()) {
    case "usd":
      return "$";
    case "eur":
      return "€";
    default:
      return "£";
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

/** Billing or trial period timeline with optional interim charge markers */
const PeriodTimeline: FC<{
  start?: string | null;
  end?: string | null;
  isTrialing?: boolean;
  interimInterval?: string | null;
  lastInterimChargeAt?: string | null;
}> = ({ start, end, isTrialing, interimInterval }) => {
  if (!start || !end) return null;

  const elapsed = elapsedPct(start, end);
  const fillColor = isTrialing
    ? "#7c3aed"
    : elapsed >= 90
    ? "#ef4444"
    : elapsed >= 70
    ? "#f59e0b"
    : "#027aff";

  const todayPct = elapsed;
  const now = Date.now();

  const dots =
    !isTrialing && interimInterval
      ? interimDatesInPeriod(start, end, interimInterval)
      : [];

  const hasPastDots = dots.some((d) => d.getTime() < now);
  const hasFutureDots = dots.some((d) => d.getTime() > now);
  const nextDot = dots.find((d) => d.getTime() > now);

  return (
    <TimelineWrap>
      <TLTrack>
        <TLFill $pct={elapsed} $color={fillColor} />
        <TodayPin $left={todayPct} />
        {dots.map((d, i) => {
          const left = pctOnTrack(d, start, end);
          const isPast = d.getTime() < now;
          const isNext = d === nextDot;
          const color = isPast ? "#10b981" : isNext ? "#f59e0b" : "#d1d5db";
          return <TLDot key={i} $left={left} $color={color} $size={12} />;
        })}
      </TLTrack>
      <TLLabels>
        <span>{fmtDate(start)}</span>
        <span>
          {Math.round(elapsed)}% elapsed &nbsp;·&nbsp;{" "}
          {(() => {
            const d = daysLeft(end);
            return d === null ? "" : `${d}d remaining`;
          })()}
        </span>
        <span>{fmtDate(end)}</span>
      </TLLabels>
      {dots.length > 0 && (
        <TLLegend>
          {hasPastDots && (
            <TLLegendItem>
              <TLLegendDot $color="#10b981" />
              past interim charge
            </TLLegendItem>
          )}
          {nextDot && (
            <TLLegendItem>
              <TLLegendDot $color="#f59e0b" />
              next expected charge ({fmtDate(nextDot.toISOString())})
            </TLLegendItem>
          )}
          {hasFutureDots && (
            <TLLegendItem>
              <TLLegendDot $color="#d1d5db" />
              future charges
            </TLLegendItem>
          )}
        </TLLegend>
      )}
    </TimelineWrap>
  );
};

const fmtProviderDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const linkedSubscriptionLabel = (
  subscriptionId: string,
  subscriptions: CompanySubscriptionDetailsEntry[]
): string => {
  const sub = subscriptions.find((s) => s.subscriptionId === subscriptionId);
  if (!sub) return subscriptionId;
  const name = [sub.subscriptionOption, sub.subscriptionLevel]
    .filter(Boolean)
    .join(" / ");
  return sub.status ? `${name} (${sub.status})` : name;
};

const BillingProvidersData: FC<{
  customers: PaymentProviderCustomerDetails[];
  subscriptions: CompanySubscriptionDetailsEntry[];
}> = ({ customers, subscriptions }) => {
  const [tab, setTab] = useState<"gocardless" | "ryft">("gocardless");
  const filtered = customers.filter(
    (c) => String(c.provider || "").toLowerCase() === tab
  );

  return (
    <div
      style={{
        border: "1px solid #e0e0e0",
        borderRadius: 10,
        padding: "12px 16px",
        background: "#fafafa",
      }}
    >
      <ProviderTabRow>
        <ProviderTab
          type="button"
          $active={tab === "gocardless"}
          onClick={() => setTab("gocardless")}
        >
          GoCardless
        </ProviderTab>
        <ProviderTab
          type="button"
          $active={tab === "ryft"}
          onClick={() => setTab("ryft")}
        >
          Ryft
        </ProviderTab>
      </ProviderTabRow>

      {filtered.length === 0 ? (
        <NoProviderData>No data</NoProviderData>
      ) : (
        filtered.map((pm) => {
          const statusBadge = getProviderStatusBadge(pm.status);
          return (
            <ProviderCard key={pm.id} $softDeleted={pm.softDeleted}>
              <ProviderCardHeader>
                {pm.isDefault && <ProviderBadge>Default</ProviderBadge>}
                {pm.softDeleted && (
                  <ProviderBadge $variant="danger">Soft deleted</ProviderBadge>
                )}
                {pm.status && (
                  <ProviderBadge $variant={statusBadge.variant}>
                    {statusBadge.label}
                  </ProviderBadge>
                )}
              </ProviderCardHeader>

              <ProviderKV>
                <ProviderK>Customer ID</ProviderK>
                <ProviderV>
                  {pm.providerCustomerDashboardUrl && pm.providerCustomerId ? (
                    <ProviderLink
                      href={pm.providerCustomerDashboardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pm.providerCustomerId}
                    </ProviderLink>
                  ) : (
                    pm.providerCustomerId || "—"
                  )}
                </ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Payment method ID</ProviderK>
                <ProviderV>
                  {pm.providerPaymentMethodDashboardUrl &&
                  pm.providerPaymentMethodId ? (
                    <ProviderLink
                      href={pm.providerPaymentMethodDashboardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {pm.providerPaymentMethodId}
                    </ProviderLink>
                  ) : (
                    pm.providerPaymentMethodId || "—"
                  )}
                </ProviderV>
              </ProviderKV>
              {pm.providerPaymentMethodReference ? (
                <ProviderKV>
                  <ProviderK>Mandate reference</ProviderK>
                  <ProviderV>{pm.providerPaymentMethodReference}</ProviderV>
                </ProviderKV>
              ) : null}
              <ProviderKV>
                <ProviderK>Brand</ProviderK>
                <ProviderV>{pm.paymentMethodBrand || "—"}</ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Last digits</ProviderK>
                <ProviderV>{pm.last4 || "—"}</ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Currency</ProviderK>
                <ProviderV>{pm.currency?.toUpperCase() || "—"}</ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Scheme</ProviderK>
                <ProviderV>{pm.scheme?.toUpperCase() || "—"}</ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Linked subscriptions</ProviderK>
                <ProviderV>
                  {pm.linkedSubscriptionIds.length
                    ? pm.linkedSubscriptionIds
                        .map((id) => linkedSubscriptionLabel(id, subscriptions))
                        .join(", ")
                    : "Not assigned"}
                </ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Status</ProviderK>
                <ProviderV>
                  {pm.status ? (
                    <ProviderBadge $variant={statusBadge.variant}>
                      {statusBadge.label}
                    </ProviderBadge>
                  ) : (
                    "—"
                  )}
                </ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Created</ProviderK>
                <ProviderV>{fmtProviderDate(pm.createdAt)}</ProviderV>
              </ProviderKV>
              <ProviderKV>
                <ProviderK>Updated</ProviderK>
                <ProviderV>{fmtProviderDate(pm.updatedAt)}</ProviderV>
              </ProviderKV>
              {pm.softDeleted && (
                <ProviderKV>
                  <ProviderK>Deleted</ProviderK>
                  <ProviderV>{fmtProviderDate(pm.deletedAt)}</ProviderV>
                </ProviderKV>
              )}
            </ProviderCard>
          );
        })
      )}
    </div>
  );
};

/** Recent invoices table */
const Invoices: FC<{ invoices: SubscriptionInvoice[] }> = ({ invoices }) => {
  const { isDownloading, isDownloadingId, downloadInvoicePdf } =
    useInvoicePdfDownload();

  if (!invoices.length) return null;
  const totalPaid = invoices
    .filter((i) => i.status === "paid")
    .reduce((s, i) => s + (i.amount_paid ?? 0), 0);

  return (
    <InvoiceWrap>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 700, color: "#555" }}>
          Recent invoices
        </span>
        {totalPaid > 0 && (
          <span style={{ fontSize: 12, color: "#10b981", fontWeight: 700 }}>
            {fmt(totalPaid)} total paid
          </span>
        )}
      </div>
      <InvoiceTableScroll>
        <InvoiceTable>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>Description</Th>
              <Th>Amount</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: "right" }} />
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => {
              const thisRowBusy = isDownloadingId(inv.id);
              return (
                <tr key={inv.id}>
                  <Td>{fmtUnix(inv.created)}</Td>
                  <Td
                    style={{
                      color: "#888",
                      maxWidth: 180,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {inv.description ??
                      inv.billing_reason?.replace(/_/g, " ") ??
                      "—"}
                  </Td>
                  <Td style={{ fontWeight: 700 }}>
                    {currSym(inv.currency)}
                    {((inv.totalPence ?? inv.amount_paid ?? 0) / 100).toFixed(
                      2
                    )}
                  </Td>
                  <Td>
                    <StatusBadge $paid={inv.status === "paid"}>
                      {inv.status}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <RowActions>
                      <ViewButton
                        type="button"
                        onClick={() =>
                          window.open(
                            `/private/subscription/invoices/${inv.id}`,
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        View
                      </ViewButton>
                      <DownloadButton
                        type="button"
                        disabled={isDownloading}
                        onClick={() => void downloadInvoicePdf(inv.id)}
                      >
                        {thisRowBusy ? "…" : "Download"}
                      </DownloadButton>
                    </RowActions>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </InvoiceTable>
      </InvoiceTableScroll>
    </InvoiceWrap>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Props & main component
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  data: FullCompanyApiType;
  onRefresh: () => void;
  onEnableStripePaymentClick: () => Promise<void>;
  onDisableStripePaymentClick: () => Promise<void>;
}

const SubscriptionSection: FC<Props> = ({
  data,
  onRefresh,
  onEnableStripePaymentClick,
  onDisableStripePaymentClick,
}) => {
  const [showWizard, setShowWizard] = useState(false);
  const [wizardLockedType, setWizardLockedType] =
    useState<SubscriptionOptionEnum | null>(null);
  const [activeOption, setActiveOption] =
    useState<SubscriptionBillingTabOption>(
      SubscriptionOptionEnum.PAYMENTS_ONLY
    );
  const [details, setDetails] = useState<CompanySubscriptionDetails | null>(
    null
  );
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [subscriptionConfirmedLoading, setSubscriptionConfirmedLoading] =
    useState(false);
  const [activatingSubscriptionId, setActivatingSubscriptionId] = useState<
    string | null
  >(null);
  const { showNotification } = useNotification();
  const { addModal, removeLastModal } = useModalManager();
  const { authUser } = useAuthUserContext();
  const adminCanDoActuinsCompanies =
    authUser.policies?.actionsOnCompanies === true;

  const isDeletedStatus = (s?: string) => isDeletedSubscriptionStatus(s);

  const allSubs = data?.subscriptions ?? [];

  // A product can have several rows (a cancelled one plus the draft that
  // carries its terms over), and the endpoint returns them unsorted —
  // prefer whichever is not deleted so the draft wins over the dead row.
  const findDetailEntry = (option: string) => {
    const forOption =
      details?.subscriptions.filter((s) => s.subscriptionOption === option) ??
      [];
    return forOption.find((s) => !isDeletedStatus(s.status)) ?? forOption[0];
  };

  const findCompanySub = (option: string) =>
    allSubs.find(
      (s: { subscriptionOption?: string; status?: string }) =>
        s.subscriptionOption === option && !isDeletedStatus(s.status)
    );

  const resolveActiveSubForOption = (
    option: string
  ): CompanySubscriptionDetailsEntry | undefined => {
    const detailEntry = findDetailEntry(option);
    if (detailEntry && !isDeletedStatus(detailEntry.status)) {
      return detailEntry;
    }
    const companySub = findCompanySub(option);
    if (!companySub) return undefined;
    return {
      subscriptionId: (companySub as any)._id
        ? String((companySub as any)._id)
        : undefined,
      subscriptionOption: companySub.subscriptionOption,
      subscriptionLevel: companySub.subscriptionLevel,
      status: companySub.status,
    } as CompanySubscriptionDetailsEntry;
  };

  const activeSub = resolveActiveSubForOption(activeOption);
  const activeSubs = activeSub ? [activeSub] : [];
  const hasActive = activeSubs.length > 0;
  const isIncompleteSubscription =
    (activeSub?.status ?? "").toLowerCase() === "incomplete";
  const isB2bTab =
    activeOption === SubscriptionOptionEnum.SHOPIFY_B2B_SUBSCRIPTION;
  const isPaymentsOnlyTab =
    activeOption === SubscriptionOptionEnum.PAYMENTS_ONLY;
  const isEuPaymentsOnlyTab =
    activeOption === SubscriptionOptionEnum.PAYMENTS_ONLY_EU;
  const isPaymentsSubscriptionTab = isPaymentsOnlyTab || isEuPaymentsOnlyTab;
  const canConfigureTab =
    isPaymentsSubscriptionTab ||
    activeOption === SubscriptionOptionEnum.TOOLKIT ||
    activeOption === SubscriptionOptionEnum.TOOLKIT_MOBILE;

  const pillTone = (
    status: ReturnType<typeof resolveSubscriptionPillStatus>
  ) => {
    switch (status) {
      case "active":
        return "#dcfce7";
      case "trialing":
        return "#dbeafe";
      case "scheduled":
        return "#fef3c7";
      case "canceled":
        return "#fee2e2";
      case "preconfigured":
        return "#ede9fe";
      default:
        return "#f3f4f6";
    }
  };

  // ── Fetch billing details ─────────────────────────────────────────────────

  // Refetch billing details whenever the underlying sub state changes,
  // not just on mount. The wizard's onSuccess callback rehydrates the
  // company doc via `getCompany` → `data.subscriptions[0]` reflects the
  // new tier/interval, but this effect was keyed only on `data?._id`
  // (the company id, which doesn't change) and `hasActive`. Result:
  // admin upgrades / downgrades didn't refresh the "current state"
  // banner until the page was reloaded. Including a derived signature
  // of the active sub here makes the refetch run whenever any
  // meaningful field flips.
  const activeSubSignature = JSON.stringify(
    allSubs
      .filter((s: { status?: string }) => !isDeletedStatus(s.status))
      .map((s: any) => ({
        subscriptionOption: s?.subscriptionOption ?? null,
        subscriptionLevel: s?.subscriptionLevel ?? null,
        subscriptionInterval: s?.subscriptionInterval ?? null,
        status: s?.status ?? null,
        updatedAt: s?.updatedAt ?? null,
      }))
  );

  const refreshBillingDetails = useCallback(async () => {
    if (!data?._id) return;
    setDetailsLoading(true);
    try {
      const nextDetails = await api.getSubscriptionDetails(data._id);
      setDetails(nextDetails);
    } catch (e) {
      console.error("[SubscriptionSection] billing details error", e);
    } finally {
      setDetailsLoading(false);
    }
  }, [data?._id]);

  useEffect(() => {
    void refreshBillingDetails();
  }, [refreshBillingDetails, hasActive, activeSubSignature]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleRemoveTrial = async () => {
    if (
      !window.confirm(
        "End the trial now? The customer will be moved to the billing phase immediately."
      )
    )
      return;
    try {
      const result = await api.removeTrial(
        data._id,
        activeSub?.subscriptionId ?? null
      );
      if (result?.success === false || result?.error) {
        showNotification(
          result?.error || result?.message || "Failed to end trial",
          NotificationTypes.DANGER
        );
      } else {
        showNotification(
          result?.message || "Trial ended",
          NotificationTypes.SUCCESS
        );
      }
      onRefresh();
    } catch (e: any) {
      // The admin-backend's error middleware emits
      // `{ success: false, message, errors: [message] }` so the real
      // reason is at `e.error.message` (or `e.error.errors[0]` as a
      // legacy fallback). The old extractor only checked
      // `e.error.message` (object key) and `e.message` (top-level),
      // missing both shapes, so every backend error became the
      // generic "Failed to end trial".
      const detail =
        (e?.error?.message && typeof e.error.message === "string"
          ? e.error.message
          : null) ??
        (Array.isArray(e?.error?.errors) &&
        typeof e.error.errors[0] === "string"
          ? e.error.errors[0]
          : null) ??
        (typeof e?.error === "string" ? e.error : null) ??
        (typeof e?.message === "string" ? e.message : null) ??
        "Failed to end trial";
      // eslint-disable-next-line no-console
      console.error("[removeTrial] failed", e);
      showNotification(detail, NotificationTypes.DANGER);
    }
  };

  const handleRemoveDiscount = async () => {
    if (!window.confirm("Remove the applied discount? This cannot be undone."))
      return;
    try {
      await api.removeDiscount(data._id);
      showNotification("Discount removed", NotificationTypes.SUCCESS);
      onRefresh();
    } catch {
      showNotification("Failed to remove discount", NotificationTypes.DANGER);
    }
  };

  const handleActivateIncompleteSubscription = async () => {
    const subscriptionId = activeSub?.subscriptionId;
    if (!subscriptionId) {
      showNotification(
        "Unable to activate: subscription id is missing",
        NotificationTypes.DANGER
      );
      return;
    }
    if (
      !window.confirm(
        "Activate this preconfigured subscription now? Period dates will start immediately and any applicable upfront invoice will be created."
      )
    ) {
      return;
    }
    setActivatingSubscriptionId(subscriptionId);
    try {
      const result = await api.activateIncompleteSubscription(
        data._id,
        subscriptionId
      );
      if (result?.success === false || result?.error) {
        showNotification(
          result?.error || result?.message || "Failed to activate subscription",
          NotificationTypes.DANGER
        );
        return;
      }
      showNotification("Subscription activated", NotificationTypes.SUCCESS);
      onRefresh();
      void refreshBillingDetails();
    } catch (e) {
      showNotification(
        getApiErrorMessage(e, "Failed to activate subscription"),
        NotificationTypes.DANGER
      );
    } finally {
      setActivatingSubscriptionId(null);
    }
  };

  // ── Derive active sub details (scoped to selected pill tab) ───────────────

  const isTrialing = activeSub?.status === "trialing";
  const isPaymentsSubscription =
    activeOption === SubscriptionOptionEnum.PAYMENTS_ONLY ||
    activeOption === SubscriptionOptionEnum.PAYMENTS_ONLY_EU ||
    activeSub?.subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY ||
    activeSub?.subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY_EU;
  const isUkPaymentsOnly =
    activeOption === SubscriptionOptionEnum.PAYMENTS_ONLY ||
    activeSub?.subscriptionOption === SubscriptionOptionEnum.PAYMENTS_ONLY;
  // ── Fee calculations ──────────────────────────────────────────────────────

  const levelUsed = activeSub?.levelUsed ?? 0;
  const txCount = activeSub?.unitCount ?? 0;
  const lastLvl = activeSub?.levelUsedAtLastInterimCharge ?? 0;
  const lastTxCount = activeSub?.unitCountAtLastInterimCharge ?? 0;

  const interimInterval = isPaymentsSubscription
    ? activeSub?.interimBillingInterval ?? null
    : null;

  const deltaVol = Math.max(0, levelUsed - lastLvl);
  const deltaTx = Math.max(0, txCount - lastTxCount);

  // ── Interim billing ───────────────────────────────────────────────────────

  const nextCharge = nextInterimCharge(
    activeSub?.lastInterimChargeAt,
    interimInterval
  );
  const nextChargeHours = nextCharge ? hoursUntil(nextCharge) : null;
  const isOverdue = nextCharge ? nextCharge.getTime() < Date.now() : false;

  const interimProgress =
    activeSub?.lastInterimChargeAt && nextCharge
      ? Math.min(
          100,
          ((Date.now() - new Date(activeSub.lastInterimChargeAt).getTime()) /
            (nextCharge.getTime() -
              new Date(activeSub.lastInterimChargeAt).getTime())) *
            100
        )
      : null;

  // ── Discount & trial ──────────────────────────────────────────────────────

  const discountLabel = (() => {
    const discount = activeSub?.subscriptionDiscount;
    if (!discount?.percentOff) return null;
    const dur =
      discount.duration === "repeating"
        ? `${discount.durationInMonths ?? "?"} months`
        : discount.duration ?? "once";
    return `${discount.percentOff}% off · ${dur}`;
  })();

  const trialLabel = (() => {
    const configLabel = (() => {
      if (activeSub?.trialPeriodMode === "none") return "No trial";
      if (activeSub?.trialPeriodMode === "custom" && activeSub.trialPeriodDays)
        return `Custom ${activeSub.trialPeriodDays}-day trial`;
      if (!activeSub?.trialPeriodMode) return "Not configured";
      const defaultDays = getDefaultTrialDays(
        activeSub?.subscriptionOption ?? SubscriptionOptionEnum.PAYMENTS_ONLY,
        !!activeSub?.hasHighRiskSubscriptionPricing
      );
      return `Default (${defaultDays} days)`;
    })();

    const subStatus = String(activeSub?.status ?? "").toLowerCase();
    const isTrialingNow =
      subStatus === "trialing" || activeSub?.onTrial === true;
    const trialExpiresAt = activeSub?.trialExpiresAt
      ? new Date(activeSub.trialExpiresAt)
      : null;
    const hadTrialEnded = Boolean(
      activeSub &&
        !isTrialingNow &&
        trialExpiresAt &&
        trialExpiresAt.getTime() <= Date.now()
    );

    if (isTrialingNow) return `${configLabel} · current sub: on trial`;
    if (hadTrialEnded) return `${configLabel} · current sub: trial ended`;
    return configLabel;
  })();

  const preconfiguredDiscountLabel = (() => {
    const discount = data.discountPercentOff;
    if (!discount) return null;
    const dur =
      data.discountDuration === "repeating"
        ? `${data.discountDurationInMonths ?? "?"} months`
        : data.discountDuration ?? "once";
    return `${discount}% off · ${dur}`;
  })();

  const preconfiguredTrialLabel = (() => {
    if (data.trialPeriodMode === "none") return "No trial";
    if (data.trialPeriodMode === "custom" && data.trialPeriodDays) {
      return `Custom ${data.trialPeriodDays}-day trial`;
    }
    const defaultDays = getDefaultTrialDays(
      SubscriptionOptionEnum.PAYMENTS_ONLY,
      !!data.hasHighRiskSubscriptionPricing
    );
    return `Default (${defaultDays} days)`;
  })();

  // Days countdown: while on trial we want days-until-trial-end (the
  // first-charge date), not days-until-currentPeriodEnd which can be a
  // different and earlier date for mid-month signups.
  const isTrialingForDays = activeSub?.status === "trialing";
  const days = daysLeft(
    isTrialingForDays
      ? activeSub?.trialExpiresAt ?? activeSub?.currentPeriodEnd
      : activeSub?.currentPeriodEnd
  );
  const urgentDays = days != null && days <= 5;

  // ── Cancellation state ────────────────────────────────────────────────────
  const subStatus = (activeSub?.status ?? "").toLowerCase();
  const isCancelledStatus =
    subStatus === "canceled" || subStatus === "cancelled";
  const isScheduledCancel =
    !!activeSub?.cancelAtPeriodEnd && !isCancelledStatus;

  // ── Subscription display name ─────────────────────────────────────────────

  const planName = (sub: {
    subscriptionOption?: SubscriptionOptionEnum;
    subscriptionLevel?: string | null;
  }) => {
    if (
      sub.subscriptionOption === SubscriptionOptionEnum.TOOLKIT ||
      sub.subscriptionOption === SubscriptionOptionEnum.TOOLKIT_MOBILE
    ) {
      return `${sub.subscriptionOption} — ${sub.subscriptionLevel ?? "?"}`;
    }
    return sub.subscriptionOption ?? "—";
  };

  const subscriptionConfirmed = data.subscriptionConfirmed ?? false;

  const onSubscriptionConfirmedChange = (nextChecked: boolean) => {
    if (!adminCanDoActuinsCompanies || nextChecked === subscriptionConfirmed)
      return;

    const enabling = nextChecked;
    addModal(
      <Modal>
        <ModalHeader
          title={
            enabling
              ? "Show Payments Only pricing"
              : "Hide Payments Only pricing"
          }
          handleClose={removeLastModal}
        />
        <ModalContent>
          <Typography variant={TypographyVariant.BODY1}>
            {enabling
              ? "Company users will see pricing for Payments Only subscriptions in their billing section. Turn this on only once pricing has been confirmed with the client."
              : "Company users will no longer see subscription pricing for Payments Only subscriptions in their billing section."}
          </Typography>
          <ModalButtonWrapper>
            <Button
              variant={ButtonVariant.CONTAINED}
              onClick={async () => {
                setSubscriptionConfirmedLoading(true);
                try {
                  await api.toggleSubscriptionConfirmed(data._id);
                  showNotification(
                    enabling
                      ? "Payments Only pricing is now visible"
                      : "Payments Only pricing is now hidden",
                    NotificationTypes.SUCCESS
                  );
                  onRefresh();
                } catch (e: unknown) {
                  showNotification(
                    getApiErrorMessage(e, "An error has occurred"),
                    NotificationTypes.WARNING
                  );
                } finally {
                  setSubscriptionConfirmedLoading(false);
                  removeLastModal();
                }
              }}
              loading={subscriptionConfirmedLoading}
              style={{ marginRight: 20 }}
            >
              Confirm
            </Button>
            <Button onClick={removeLastModal} variant={ButtonVariant.OUTLINED}>
              Cancel
            </Button>
          </ModalButtonWrapper>
        </ModalContent>
      </Modal>
    );
  };

  if (!data) return null;

  const openConfigureWizard = () => {
    if (activeOption === SubscriptionOptionEnum.SHOPIFY_B2B_SUBSCRIPTION) {
      return;
    }
    setWizardLockedType(activeOption);
    setShowWizard(true);
  };

  const upcomingInvoiceAmount =
    activeSub?.upcomingInvoiceAmount ??
    activeSub?.upcomingInvoice?.amountDue ??
    null;

  return (
    <Wrap>
      <PillBar>
        {SUBSCRIPTION_BILLING_TABS.map((tab) => {
          const entry = resolveActiveSubForOption(tab.option);
          const pillStatus = resolveSubscriptionPillStatus(entry);
          return (
            <PillTab
              key={tab.option}
              type="button"
              $active={activeOption === tab.option}
              onClick={() => setActiveOption(tab.option)}
            >
              {tab.label}
              <PillBadge $tone={pillTone(pillStatus)}>
                {PILL_STATUS_LABEL[pillStatus]}
              </PillBadge>
            </PillTab>
          );
        })}
      </PillBar>

      {isPaymentsOnlyTab && (
        <TabHeader>
          <Typography variant={TypographyVariant.HEADER3}>
            Payments Only
          </Typography>

          <SectionHeaderActions>
            <SubscriptionConfirmedRow>
              <Typography variant={TypographyVariant.BODY2}>
                Subscription pricing confirmed
              </Typography>
              <TooltipText
                id="subscription-confirmed-tooltip"
                text="Enable this once the subscription pricing for Payments sub has been finalized between sales and the client. Enabling this will display the actual subscription price on the user's billing page and clear the unconfirmed price warnings."
              >
                <span style={{ cursor: "help" }}>ⓘ</span>
              </TooltipText>
              <SwitchButton
                checked={subscriptionConfirmed}
                onChange={onSubscriptionConfirmedChange}
                disabled={
                  !adminCanDoActuinsCompanies || subscriptionConfirmedLoading
                }
              />
            </SubscriptionConfirmedRow>
            {hasActive && (
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={openConfigureWizard}
              >
                Customise Subscription
              </Button>
            )}
            {!hasActive && (
              <Button
                variant={ButtonVariant.OUTLINED}
                onClick={openConfigureWizard}
              >
                Pre-configure Subscription
              </Button>
            )}
          </SectionHeaderActions>
        </TabHeader>
      )}

      {isEuPaymentsOnlyTab && (
        <TabHeader>
          <Typography variant={TypographyVariant.HEADER3}>
            EU Open Banking Payments
          </Typography>
          <SectionHeaderActions>
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={openConfigureWizard}
            >
              {hasActive
                ? "Customise Subscription"
                : "Pre-configure Subscription"}
            </Button>
          </SectionHeaderActions>
        </TabHeader>
      )}

      {canConfigureTab && !isPaymentsSubscriptionTab && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <Typography variant={TypographyVariant.HEADER3}>
            {activeOption === SubscriptionOptionEnum.TOOLKIT
              ? "Toolkit"
              : "Toolkit Mobile"}
          </Typography>
          {hasActive && (
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={openConfigureWizard}
            >
              Customise Subscription
            </Button>
          )}
          {!hasActive && (
            <Button
              variant={ButtonVariant.OUTLINED}
              onClick={openConfigureWizard}
            >
              Pre-configure Subscription
            </Button>
          )}
        </div>
      )}

      {isB2bTab && (
        <Typography
          variant={TypographyVariant.HEADER3}
          style={{ marginBottom: 12 }}
        >
          Shopify B2B
        </Typography>
      )}

      {!hasActive && !isPaymentsSubscriptionTab && (
        <EmptyTabState>
          <div>
            {isB2bTab
              ? "No Shopify B2B subscription for this company."
              : `No ${
                  activeOption === SubscriptionOptionEnum.TOOLKIT
                    ? "Toolkit"
                    : "Toolkit Mobile"
                } subscription for this company.`}
          </div>
          <div>
            {canConfigureTab
              ? PRECONFIGURATION_AVAILABLE_MESSAGE
              : PRECONFIGURATION_UNAVAILABLE_MESSAGE}
          </div>
        </EmptyTabState>
      )}

      {isEuPaymentsOnlyTab && !hasActive && (
        <EmptyTabState>
          <div>No EU Open Banking Payments subscription for this company.</div>
          <div>{PRECONFIGURATION_AVAILABLE_MESSAGE}</div>
        </EmptyTabState>
      )}

      {/* ── No active subscription — PO pre-configured preview ─────────── */}
      {isPaymentsOnlyTab &&
        !hasActive &&
        (() => {
          const hasAnyConfig =
            data.customFlatFeePerTransaction != null ||
            data.customPercentageFee != null ||
            data.hasBespokePricing ||
            data.hasHighRiskSubscriptionPricing ||
            data.paymentsInterimBillingInterval ||
            data.preRegistration ||
            data.trialPeriodMode === "custom" ||
            data.trialPeriodMode === "none" ||
            data.discountPercentOff ||
            //standart Payments only tier  after pricing confirmed
            (data.subscriptionConfirmed &&
              !data.hasHighRiskSubscriptionPricing);

          if (!hasAnyConfig) {
            return (
              <EmptyCard>
                <div>No Payments Only subscription configured yet.</div>
                <div>{PRECONFIGURATION_UNAVAILABLE_MESSAGE}</div>
              </EmptyCard>
            );
          }

          // Build pricing label
          const pricingParts: string[] = [];
          if (data.hasBespokePricing) pricingParts.push("Bespoke");
          else if (data.hasHighRiskSubscriptionPricing)
            pricingParts.push("High Risk");
          else {
            if (data.customFlatFeePerTransaction != null)
              pricingParts.push(`${fmt(data.customFlatFeePerTransaction)}/tx`);
            if (data.customPercentageFee != null)
              pricingParts.push(`${data.customPercentageFee}%`);
            if (data.customPercentageFeeMinimum != null)
              pricingParts.push(
                `min ${fmt(data.customPercentageFeeMinimum)}/mo`
              );
          }
          const pricingLabel =
            pricingParts.length > 0 ? pricingParts.join(" + ") : "Standard";

          return (
            <PreviewCard>
              <PreviewOverlay />
              <PreviewBadge>
                {data.preRegistration
                  ? "Pre-registered"
                  : "Awaiting subscription"}
              </PreviewBadge>
              <PreviewTitle>Pre-configured subscription</PreviewTitle>
              <PreviewGrid>
                <PreviewItem>
                  <PreviewItemLabel>Pricing</PreviewItemLabel>
                  <PreviewItemValue>{pricingLabel}</PreviewItemValue>
                </PreviewItem>
                <PreviewItem>
                  <PreviewItemLabel>Trial</PreviewItemLabel>
                  <PreviewItemValue>{preconfiguredTrialLabel}</PreviewItemValue>
                </PreviewItem>
                {data.paymentsInterimBillingInterval && (
                  <PreviewItem>
                    <PreviewItemLabel>Interim billing</PreviewItemLabel>
                    <PreviewItemValue>
                      {data.paymentsInterimBillingInterval
                        .charAt(0)
                        .toUpperCase() +
                        data.paymentsInterimBillingInterval.slice(1)}
                    </PreviewItemValue>
                  </PreviewItem>
                )}
                {preconfiguredDiscountLabel && (
                  <PreviewItem>
                    <PreviewItemLabel>Discount</PreviewItemLabel>
                    <PreviewItemValue>
                      {preconfiguredDiscountLabel}
                    </PreviewItemValue>
                  </PreviewItem>
                )}
              </PreviewGrid>
              <PreviewHint>
                These settings will be applied when the customer purchases their
                subscription.
              </PreviewHint>
            </PreviewCard>
          );
        })()}

      {/* ── Cockpit ─────────────────────────────────────────────────────── */}
      {hasActive && (
        <Cockpit>
          {/* ── Header: plan name + status ── */}
          <CockpitHeader>
            <div>
              {activeSubs.map((sub, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: i < activeSubs.length - 1 ? 6 : 0,
                  }}
                >
                  <PlanTitle>{planName(sub)}</PlanTitle>
                  <Status status={sub?.status ?? "processing"} />
                </div>
              ))}
            </div>
            <PlanMeta>
              {isIncompleteSubscription && (
                <>
                  <PlanTag $variant="purple">Preconfigured</PlanTag>
                  <Button
                    variant={ButtonVariant.CONTAINED}
                    onClick={handleActivateIncompleteSubscription}
                    loading={
                      activatingSubscriptionId === activeSub?.subscriptionId
                    }
                  >
                    Activate
                  </Button>
                </>
              )}
              {data.preRegistration && (
                <PlanTag $variant="green">✓ Pre-registered</PlanTag>
              )}
              {isPaymentsSubscription && activeSub?.hasBespokePricing && (
                <PlanTag $variant="purple">Bespoke pricing</PlanTag>
              )}
              {isUkPaymentsOnly &&
                activeSub?.hasHighRiskSubscriptionPricing && (
                  <PlanTag $variant="orange">High Risk</PlanTag>
                )}
              {discountLabel && (
                <PlanTag $variant="green">🏷 {discountLabel}</PlanTag>
              )}
            </PlanMeta>
          </CockpitHeader>

          {!detailsLoading && activeSub && isScheduledCancel && (
            <CancellationBanner $variant="scheduled">
              <CancellationBannerIcon>⏳</CancellationBannerIcon>
              <CancellationBannerBody>
                <strong>Cancellation scheduled.</strong> Subscription will end
                on <strong>{fmtDate(activeSub.currentPeriodEnd)}</strong>
                {activeSub.cancellationRequestedAt && (
                  <> (requested {fmtDate(activeSub.cancellationRequestedAt)})</>
                )}
                . Customer keeps full access until then and can resume any time
                before that date.
                {activeSub.cancellationReason && (
                  <CancellationBannerReason>
                    Reason: {activeSub.cancellationReason}
                  </CancellationBannerReason>
                )}
              </CancellationBannerBody>
            </CancellationBanner>
          )}

          {!detailsLoading && activeSub && isCancelledStatus && (
            <CancellationBanner $variant="cancelled">
              <CancellationBannerIcon>✕</CancellationBannerIcon>
              <CancellationBannerBody>
                <strong>Subscription cancelled</strong>
                {activeSub.canceledAt && (
                  <>
                    {" "}
                    on <strong>{fmtDate(activeSub.canceledAt)}</strong>
                  </>
                )}
                {activeSub.cancellationRequestedAt &&
                  activeSub.canceledAt !==
                    activeSub.cancellationRequestedAt && (
                    <>
                      {" "}
                      (requested {fmtDate(activeSub.cancellationRequestedAt)})
                    </>
                  )}
                .
                {activeSub.cancellationReason && (
                  <CancellationBannerReason>
                    Reason: {activeSub.cancellationReason}
                  </CancellationBannerReason>
                )}
              </CancellationBannerBody>
            </CancellationBanner>
          )}

          {detailsLoading && (
            <SkeletonSection>
              {/* Skeleton: timeline */}
              <div>
                <SkeletonBar $w="80px" $h="10px" />
                <div style={{ marginTop: 10 }} />
                <SkeletonBar $h="10px" />
                <SkeletonRow
                  style={{ marginTop: 8, justifyContent: "space-between" }}
                >
                  <SkeletonBar $w="60px" $h="9px" />
                  <SkeletonBar $w="60px" $h="9px" />
                </SkeletonRow>
              </div>
              {/* Skeleton: metric cards */}
              <SkeletonRow>
                <SkeletonBar $h="70px" />
                <SkeletonBar $h="70px" />
                <SkeletonBar $h="70px" />
              </SkeletonRow>
              {/* Skeleton: rows */}
              <SkeletonBar $w="65%" $h="11px" />
              <SkeletonBar $w="45%" $h="11px" />
            </SkeletonSection>
          )}

          {!detailsLoading && activeSub && (
            <>
              {/* ── Billing / Trial period timeline ── */}
              <CSection $delay={1}>
                <SectionHeader>
                  <SLabel>
                    {isIncompleteSubscription
                      ? "Preconfigured subscription"
                      : isTrialing
                      ? "Trial period"
                      : "Billing period"}
                  </SLabel>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {!isIncompleteSubscription && days != null && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: urgentDays ? "#ef4444" : "#888",
                        }}
                      >
                        {days}d remaining
                      </span>
                    )}
                    {isTrialing && (
                      <MiniBtn onClick={handleRemoveTrial}>
                        End trial now
                      </MiniBtn>
                    )}
                  </div>
                </SectionHeader>

                {isIncompleteSubscription ? (
                  <NextPhase>
                    <NextPhaseContent>
                      <NextPhasePrimary>
                        This subscription is configured but not active yet.
                      </NextPhasePrimary>
                      <NextPhaseSub>
                        Activate it from this page, or let the customer activate
                        it from their billing page. Billing period dates are set
                        only when activation starts.
                      </NextPhaseSub>
                    </NextPhaseContent>
                  </NextPhase>
                ) : (
                  <PeriodTimeline
                    start={activeSub.currentPeriodStart}
                    end={activeSub.currentPeriodEnd}
                    isTrialing={isTrialing}
                    interimInterval={interimInterval}
                    lastInterimChargeAt={activeSub.lastInterimChargeAt}
                  />
                )}

                {/* Upcoming invoice */}
                {!isIncompleteSubscription && upcomingInvoiceAmount != null && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginTop: 6,
                    }}
                  >
                    <span style={{ fontSize: 12, color: "#888" }}>
                      Upcoming invoice
                    </span>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: "#10b981",
                      }}
                    >
                      {fmt(upcomingInvoiceAmount)}
                    </span>
                  </div>
                )}

                {/* Next phase preview when on trial.
                    The first charge happens at TRIAL EXPIRY via the
                    trial-expiry cron (`expireTrials`), NOT at
                    currentPeriodEnd. For mid-month signups the trial
                    end can fall AFTER currentPeriodEnd, so showing
                    "billing starts {currentPeriodEnd}" was misleading.
                    Prefer trialExpiresAt; fall back only if missing. */}
                {isTrialing &&
                  (activeSub.trialExpiresAt || activeSub.currentPeriodEnd) && (
                    <NextPhase>
                      <span style={{ fontSize: 16 }}>📅</span>
                      <NextPhaseContent>
                        <NextPhasePrimary>
                          After trial — first charge on{" "}
                          <strong>
                            {fmtDate(
                              activeSub.trialExpiresAt ??
                                activeSub.currentPeriodEnd
                            )}
                          </strong>
                        </NextPhasePrimary>
                        <NextPhaseSub>
                          Charge runs at the next billing cron tick after
                          trial-end (hourly between 8 AM and 11 PM UK).
                          {isPaymentsSubscription
                            ? " Usage from this period is invoiced then."
                            : " Prorated for the remainder of the current period; full monthly price billed thereafter."}
                        </NextPhaseSub>
                      </NextPhaseContent>
                    </NextPhase>
                  )}
              </CSection>

              {/* ── Interim billing section ── */}
              {isUkPaymentsOnly &&
                !isIncompleteSubscription &&
                interimInterval && (
                  <CSection $delay={3}>
                    <SectionHeader>
                      <SLabel>
                        Interim billing —{" "}
                        {interimInterval.charAt(0).toUpperCase() +
                          interimInterval.slice(1)}
                      </SLabel>
                      {nextCharge &&
                        (isOverdue ? (
                          <Chip $variant="danger">
                            ⚡ Overdue — runs next cron
                          </Chip>
                        ) : (
                          <Chip $variant="warn">
                            ⏱ in {fmtHours(nextChargeHours ?? 0)} ·{" "}
                            {fmtDate(nextCharge.toISOString())}
                          </Chip>
                        ))}
                    </SectionHeader>

                    <MetricRow style={{ marginTop: 0 }}>
                      <MetricBox $accent="#f59e0b">
                        <MLabel>Vol since charge</MLabel>
                        <MValue style={{ fontSize: 14 }}>
                          {fmtVol(deltaVol)}
                        </MValue>
                      </MetricBox>
                      <MetricBox $accent="#027aff">
                        <MLabel>Tx since charge</MLabel>
                        <MValue style={{ fontSize: 14 }}>
                          {deltaTx.toLocaleString()}
                        </MValue>
                      </MetricBox>
                    </MetricRow>

                    <div style={{ marginTop: 10 }}>
                      <KV>
                        <KKey>Last interim charge</KKey>
                        <KVal>{fmtDate(activeSub.lastInterimChargeAt)}</KVal>
                      </KV>
                    </div>

                    {interimProgress != null && (
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            height: 5,
                            borderRadius: 3,
                            background: "#eee",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              height: "100%",
                              width: `${Math.min(interimProgress, 100)}%`,
                              background:
                                interimProgress >= 100
                                  ? "#ef4444"
                                  : interimProgress >= 80
                                  ? "#f59e0b"
                                  : "#10b981",
                              borderRadius: 3,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 10,
                            color: "#bbb",
                            marginTop: 3,
                          }}
                        >
                          <span>since last charge</span>
                          <span>
                            {Math.round(interimProgress)}% of interval
                          </span>
                        </div>
                      </div>
                    )}
                  </CSection>
                )}
            </>
          )}

          {/* ── Pricing configuration ──────────────────────────────────── */}
          {activeSub && (
            <CSection $delay={4}>
              <SLabel style={{ marginBottom: 12 }}>
                Pricing configuration
              </SLabel>
              <SubscriptionPricingBreakdown sub={activeSub} />
            </CSection>
          )}

          {/* ── Trial & Discount ─────────────────────────────────────────── */}
          <CSection $delay={5}>
            <SLabel style={{ marginBottom: 10 }}>Trial &amp; Discount</SLabel>
            <div>
              <KV>
                <KKey>Trial configuration</KKey>
                <KVal>{trialLabel}</KVal>
              </KV>
              <KV>
                <KKey>Applied discount</KKey>
                <KVal>
                  {discountLabel ? (
                    <>
                      <PlanTag $variant="green">🏷 {discountLabel}</PlanTag>
                      {data.stripeCouponId && (
                        <MiniBtn onClick={handleRemoveDiscount}>Remove</MiniBtn>
                      )}
                    </>
                  ) : (
                    <span style={{ color: "#ccc" }}>None</span>
                  )}
                </KVal>
              </KV>
            </div>
          </CSection>

          <SubscriptionCollectionsCases
            companyId={data._id}
            automatedBillingEnabled={data.automatedBillingEnabled}
            activeSubscription={activeSub}
            canManage={adminCanDoActuinsCompanies}
          />
        </Cockpit>
      )}

      {/* ── Recent invoices ──────────────────────────────────────────────── */}
      {details?.recentInvoices && details.recentInvoices.length > 0 && (
        <Invoices invoices={details.recentInvoices} />
      )}

      {/* ── Billing Providers data ─────────────────────────────────────── */}
      <div style={{ marginTop: 28, marginBottom: 12 }}>
        <Typography variant={TypographyVariant.HEADER3}>
          Billing Providers data
        </Typography>
      </div>
      <BillingProviderRow>
        <div>
          <BillingProviderLabel>
            Billing provider permission
          </BillingProviderLabel>
          <BillingProviderCaption>
            Which provider this company is allowed to use — individual
            subscriptions still resolve their own payment method.
          </BillingProviderCaption>
        </div>
        <BillingProviderSelect
          value={data.preferredBillingProvider ?? ""}
          onChange={async (e) => {
            try {
              await api.post(`/company/${data._id}/set-billing-provider`, {
                preferredBillingProvider: e.target.value || null,
              });
              showNotification(
                "Billing provider permission updated",
                NotificationTypes.SUCCESS
              );
              onRefresh();
              void refreshBillingDetails();
            } catch (err) {
              showNotification(
                getApiErrorMessage(
                  err,
                  "Failed to update billing provider permission"
                ),
                NotificationTypes.DANGER
              );
            }
          }}
        >
          <option value="">None</option>
          {data.preferredBillingProvider === "ryft" && (
            <option value="ryft">Ryft (Card)</option>
          )}
          <option value="gocardless">GoCardless (Direct Debit)</option>
          <option value="manual">Manual (Invoice Only)</option>
        </BillingProviderSelect>
        <BillingProviderConsent>
          {data.directDebitConsentEnabled
            ? "DD consent: Enabled"
            : "DD consent: Not given"}
        </BillingProviderConsent>
      </BillingProviderRow>
      <BillingProvidersData
        customers={details?.paymentProviderCustomers ?? []}
        subscriptions={details?.subscriptions ?? []}
      />

      {/* ── Subscription history ─────────────────────────────────────────── */}
      <SubscriptionHistory
        entries={details?.subscriptionHistory ?? []}
        loading={detailsLoading && !details}
      />

      {/* Card Payments (Stripe Connect) — hidden, no longer using Stripe */}
      {/* TODO: Re-enable with Ryft when card payments are back */}

      {/* ── Wizard ──────────────────────────────────────────────────────── */}
      {showWizard && wizardLockedType && (
        <SubscriptionWizardModal
          data={data}
          lockedType={wizardLockedType}
          activeSub={activeSub}
          onClose={() => {
            setShowWizard(false);
            setWizardLockedType(null);
          }}
          onSuccess={() => {
            onRefresh();
            void refreshBillingDetails();
          }}
        />
      )}
    </Wrap>
  );
};

export default SubscriptionSection;
